<template>
  <div
    class="board-list-wrap"
    :class="{
      'empty-group': isEmptyGroup,
    }"
    @mouseenter="mouseEnterList"
    @mouseleave="mouseLeaveList"
  >
    <div
      v-if="showSortBackground"
      class="sort-background"
      :class="[
        { 'rounded-none': isCardsLayoutTimeline || isCardsLayoutList },
        { 'w-full': isCardsLayoutList },
      ]"
      :style="isCardsLayoutTimeline ? { width: `${timelineSidebarWidth}px`, left: 0 } : {}"
    >
      {{ sortedByLabel }}
    </div>
    <vue-scroll
      ref="listContent"
      class="list-content"
      :ops="scrollOps"
      @handle-scroll="handleScroll"
    >
      <draggable
        v-model="listCards"
        :data-list-id="listId"
        :item-key="(cardId) => cardId"
        group="cards"
        class="draggable-cards"
        :class="[
          { 'card-dragging': mouseOverList && cardDragging },
          { 'list-scrolled-down': scrollTop > 0 },
          { 'scroll-no-drag': scrolling && !scrollOnDrag },
        ]"
        :disabled="draggingDisabled"
        filter="
          .edit-card-title,
          .title-input .__bar-is-vertical,
          .status-picker-dropdown,
          .timeline-card-resizer
        "
        :prevent-on-filter="false"
        :animation="false"
        :invert-swap="true"
        :fallback-tolerance="1"
        :move="checkMove"
        :draggable="'.list-row'"
        :drag-class="draggingClass"
        :handle="timelineDragHandleClasses"
        :ghost-class="'list-card-drop'"
        :scroll-sensitivity="scrollSensitivity"
        :scroll-speed="scrollSpeed"
        :scroll-fn="displayProjectPreviewBoards || embed ? null : onDragScroll"
        :force-fallback="true"
        :fallback-on-body="true"
        @start="dragStart"
        @end="dragEnd"
        @add="addCard"
        @update="moveCard"
      >
        <template #item="{ element: cardId, index: lIndex }">
          <div
            :key="`card-${listId}-${cardId}`"
            class="list-row usn"
            :class="`card-layout-${cardsLayout}`"
            :data-card-id="cardId"
            :data-list-id="listId"
            :data-last-hovered="lastHoveredCardId === cardId && isLastHoveredCardListId"
            :data-selected-card="selectedCardId === cardId && isLastHoveredCardListId"
            :data-multi-selected="isCardMultiSelected(cardId)"
            :data-board-id="currentBoardId"
          >
            <BoardListCard
              v-if="isCardVisible && !isCardsLayoutTimeline"
              :read-only="readOnly"
              :card-id="cardId"
              :list-id="listId"
              :index="lIndex"
              :large-board="largeBoard"
              :list-cards="listCards"
              :parent-list-id="parentListId"
              @scrollCard="scrollCard"
              @cardMouseOver="cardHover"
            />
            <TimelineListItem
              v-if="isCardVisible && isCardsLayoutTimeline"
              :card-id="cardId"
              :list-id="listId"
              :index="lIndex"
              :large-board="largeBoard"
              :position="position"
              :container-width="containerWidth"
              :list-cards="listCards"
              :parent-list-id="parentListId"
            />
            <CancelDragLabel />
          </div>
        </template>
        <template
          v-if="showListHeader && !groupByNone"
          #header
        >
          <BoardListHeader
            :list-id="listId"
            :header-footer-style="headerFooterStyle"
            :expandable="expandable"
            :show-status-icon-picker="showStatusIconPicker"
            :disable-status-icon-picker="disableStatusIconPicker"
            :list-status-icon="listStatusIcon"
            :list-status-color="listStatusColor"
            :group-by="boardGroupBy"
            :list-cards="listCards"
            :visible="visible"
            :is-cards-layout-timeline="isCardsLayoutTimeline"
            :is-epic-board="isEpicBoard"
            @headerMouseEnter="headerMouseEnter"
            @headerMouseOut="headerMouseOut"
          />
        </template>
        <template #footer>
          <div
            v-if="showListFooter"
            draggable="false"
            class="list-footer"
            :class="[{ 'self-start': isCardsLayoutTimeline }]"
            :data-list-id="list.id"
            :style="headerFooterStyle"
          >
            <DraftCardCreate
              v-if="showDraftCardCreateButton"
              class="list-row"
              :list-id="list.id"
              :parent-list-id="parentListId"
              :list-title="list?.title || ''"
              @scrollBoardListToEnd="scrollBoardListToEnd"
              @hideCreateCardView="hideCreateCardView"
            />
            <AddNewCardButton
              v-if="showAddButton"
              :list-id="list.id"
              :parent-list-id="parentListId"
            />
            <div
              v-if="inEpicBoard"
              class="epic-list-footer"
            ></div>
          </div>
        </template>
      </draggable>
    </vue-scroll>
  </div>
</template>
<script>
import BoardListCard from '@/components/widgets/BoardListCard';
import DraftCardCreate from '@/components/widgets/DraftCardCreate';
import AddNewCardButton from '@/components/widgets/AddNewCardButton';
import draggableMixin from '@/mixins/draggable';
import boardListMixin from '@/mixins/boardListMixin';
import EventBus from '@/utilities/eventBus';
import isEditable from '@/mixins/isEditable';
import draggingEnd from '@/mixins/draggingEnd';
import customIconsMixin from '@/mixins/customIconsMixin';
import elementFocusMixin from '@/mixins/elementFocusMixin';
import cardStatuses from '@/mixins/cardStatuses';
import hideDropdownsMixin from '@/mixins/hideDropdownsMixin';
import TimelineListItem from '@/components/widgets/TimelineListItem';
import CancelDragLabel from '@/components/widgets/CancelDragLabel';
import { groupByOption } from '@/types/groupByOption';
import useBoardEmbed from '@/boards/composables/useBoardEmbed';
import { ResourceType } from '@/types/resources';
import useIsBrowser from '@/utilities/composables/useIsBrowser.ts';
import usePriorityOptions from '@/utilities/composables/usePriorityOptions';
import { getCardStatus } from '@/utilities/defaultCardStatuses';
import useHandleMoveCardGroupBy from '@/utilities/composables/useHandleMoveCardGroupBy';
import cardStatusType from '@/types/cardStatusType';
import BoardListHeader from '@/components/widgets/BoardListHeader.vue';
import useGetBoardList from '@/utilities/composables/useGetBoardList';
import useCurrentRole from '@/utilities/composables/useCurrentRole';
import { toRef } from 'vue';

export default {
  name: 'BoardList',
  components: {
    AddNewCardButton,
    BoardListCard,
    DraftCardCreate,
    TimelineListItem,
    CancelDragLabel,
    BoardListHeader,
  },
  mixins: [
    draggableMixin,
    boardListMixin,
    isEditable,
    draggingEnd,
    customIconsMixin,
    elementFocusMixin,
    cardStatuses,
    hideDropdownsMixin,
  ],
  props: {
    listId: {
      type: [String, Number],
      required: true,
    },
    index: {
      type: Number,
      required: true,
      default: 0,
    },
    largeBoard: {
      type: Boolean,
      required: false,
      default: false,
    },
    visible: {
      type: Boolean,
      required: false,
      default: false,
    },
    expandable: {
      type: Boolean,
      default: false,
    },
    position: {
      type: Number,
      required: true,
    },
    containerWidth: {
      type: Number,
      required: true,
    },
    sprintDisableDrag: {
      type: Boolean,
      required: false,
      default: false,
    },
    showAddCardButton: {
      type: Boolean,
      required: false,
      default: true,
    },
    showListHeader: {
      type: Boolean,
      required: false,
      default: true,
    },
    parentListId: {
      type: [String, Number],
      required: false,
      default: '',
    },
    groupBy: {
      type: String,
      required: false,
      default: '',
    },
    // eslint-disable-next-line vue/no-unused-properties
    subGroupCards: {
      // used in boardListMixin
      type: Array,
      required: false,
      default: () => [],
    },
    // eslint-disable-next-line vue/no-unused-properties
    groupByObject: {
      // used in boardListMixin
      type: Object,
      required: false,
      default: () => {},
    },
  },
  emits: ['isEmptyGroup', 'hideCreateCardView'],
  setup(props) {
    // this inject is added because the component is using boardListMixin which is using the inject
    const { embeddedBoardAttributes, embed, inEpicBoard } = useBoardEmbed();
    const { isSafari } = useIsBrowser();
    const { priorityOptions } = usePriorityOptions();
    const { handleGroupByDrag } = useHandleMoveCardGroupBy();
    const { isGuestReader } = useCurrentRole();

    const groupByRef = toRef(props, 'groupBy');
    const listIdRef = toRef(props, 'listId');

    const { list } = useGetBoardList(groupByRef, listIdRef, props.parentListId || '');

    return {
      embeddedBoardAttributes,
      embed,
      inEpicBoard,
      isSafari,
      priorityOptions,
      handleGroupByDrag,
      list,
      isGuestReader,
    };
  },
  data() {
    return {
      listTitleFocused: false,
      localTitle: undefined,
      continueScroll: false,
      scrollOps: {
        scrollPanel: { scrollingX: false, scrollingY: true },
        rail: { gutterOfSide: '2px', gutterOfEnds: '66px' },
        bar: { size: '4px', keepShow: false },
        vuescroll: { detectResize: false },
      },
      scrollOnDrag: false,
      scrollTop: 0,
      scrollingUp: false,
      scrolling: false,
      scrollingTimeout: null,
      oldTitle: '',
      scrollSpeedBase: 30,
      scrollSpeed: 10,
      scrollSensitivity: 150,
      mouseOverList: false,
    };
  },
  computed: {
    _setupProxy() {
      return null;
    },
    isExpanded() {
      return this.$store.getters.getListExpanded(this.currentBoardId, this.listId);
    },
    showListFooter() {
      return !this.expandable || (this.expandable && this.isExpanded);
    },
    disableStatusIconPicker() {
      return !this.groupByStatus || this.readOnly || this.inEpicBoard || this.isSprint;
    },
    listTitle: {
      get() {
        return this.list.title || '';
      },
      set(title) {
        this.localTitle = title.trim();
      },
    },
    groupByStatus() {
      return this.boardGroupBy === groupByOption.status;
    },
    groupByStatusType() {
      return this.boardGroupBy === groupByOption.statusType;
    },
    groupByAssignee() {
      return this.boardGroupBy === groupByOption.assignee;
    },
    isUnassignedListId() {
      return this.listId === `${this.currentBoardId}-unassigned`;
    },
    groupByEpic() {
      return this.boardGroupBy === groupByOption.epic;
    },
    isRolledoverList() {
      return this.list.orginalId === this.$constants.rolledOverList;
    },
    isEmptyGroup() {
      if (this.parentListId !== '') {
        return false;
      }
      if (this.boardGroupBy === groupByOption.status) {
        return this.hideEmptyGroups && !this.listCards.length;
      }
      return this.hideEmptyGroups && !this.listCardsGroupByOrder.length;
    },
    listStatusIcon() {
      return (
        (getCardStatus(this.list.behavior, this.isEpicBoard) || {}).icon ||
        this.list.icon ||
        getCardStatus(cardStatusType.Backlog).icon ||
        ''
      );
    },
    listStatusColor() {
      return (
        this.list.color ||
        (this.cardStatuses[this.list.behavior] || {}).color ||
        this.cardStatuses.backlog.color ||
        ''
      );
    },
    boardSortedBy() {
      return this.embed
        ? this.embeddedBoardAttributes?.sortBy
        : this.$store.getters.getBoardSortBy(this.currentBoardId);
    },
    sortedByLabel() {
      let boardSortedBy;
      switch (this.boardSortedBy) {
        case 'time_created':
          boardSortedBy = 'timeCreatedSort';
          break;
        case 'time_updated':
          boardSortedBy = 'timeUpdatedSort';
          break;
        case 'due_date':
          boardSortedBy = 'dueDateSort';
          break;
        case 'priority':
          boardSortedBy = 'priority';
          break;
        case 'estimate':
          boardSortedBy = 'estimate';
          break;
      }
      return `${this.translate('sortedBy')} ${this.translate(boardSortedBy)}`;
    },
    cardsLayout() {
      if (this.embed) {
        return (
          this.embeddedBoardAttributes?.layout ||
          this.$store.getters.cardsLayout(this.currentBoardId)
        );
      }
      return this.$store.getters.cardsLayout();
    },
    isCardsLayoutList() {
      return !!this.currentBoardId && this.cardsLayout === 'list';
    },
    isCardsLayoutTimeline() {
      return this.currentBoardId && this.cardsLayout === 'timeline';
    },
    timelineDragHandleClasses() {
      return this.isCardsLayoutTimeline ? '.timeline-sidebar-item' : '';
    },
    draggingClass() {
      return this.isCardsLayoutTimeline ? 'timeline-dragging-card' : 'dragging-card';
    },
    timelineSidebarWidth() {
      return this.$store.getters.getTimelineSidebarWidth;
    },
    lastHoveredCardId() {
      return this.$store.getters.lastHoveredCardId;
    },
    isLastHoveredCardListId() {
      return this.$store.getters.lastHoveredListId === this.listId;
    },
    selectedCardId() {
      return this.$store.getters.selectedCardId;
    },
    multiSelectedCardIds() {
      return this.$store.getters.multiSelectedCardIds;
    },
    isMultiSelectActive() {
      return this.$store.getters.isMultiSelectActive;
    },
    isCardMultiSelected() {
      return (cardId) => this.$store.getters.isCardMultiSelected(cardId);
    },
    readOnlyAllowDragCards() {
      return this.embeddedBoardAttributes?.allowDragCard || false;
    },
    draggingDisabled() {
      return (
        !this.visible ||
        this.isMobile ||
        (this.readOnly && !this.readOnlyAllowDragCards) ||
        this.sprintDisableDrag ||
        this.inEpicBoard
      );
    },
    showListActionsButton() {
      return (
        this.visible &&
        (!this.expandable || (this.expandable && this.isExpanded) || this.actionsDropdownIsShown) &&
        !this.isMobile &&
        !this.inEpicBoard
      );
    },
    displayProjectPreviewBoards() {
      return this.$store.getters.displayProjectPreviewBoards;
    },
    infoSidebar() {
      return this.$store.getters.displayInfoSidebar;
    },
    isCardVisible() {
      return !this.expandable || (this.expandable && this.isExpanded);
    },
    headerFooterStyle() {
      return {
        ...(this.isCardsLayoutTimeline && {
          maxWidth: `${this.timelineSidebarWidth - 1}px !important`,
        }),
      };
    },
    sortByApplied() {
      return this.boardSortedBy !== 'manual';
    },
    showSortBackground() {
      return (
        this.sortByApplied &&
        this.mouseOverList &&
        this.draggingIsOn &&
        [ResourceType.Card, ResourceType.Epic].includes(this.draggedEntity.entityName) &&
        this.dropTargetId === `l-${this.listId}`
      );
    },
    actionsDropdownIsShown() {
      return this.$store.getters.getCurrentListIdActionEdit === this.list.id;
    },
    showStatusIconPicker() {
      return (
        this.groupByStatus ||
        this.groupByStatusType ||
        this.boardGroupBy === 'tag' ||
        this.boardGroupBy === 'priority' ||
        this.boardGroupBy === groupByOption.space ||
        this.isUnassignedListId
      );
    },
    groupByNone() {
      return this.boardGroupBy === groupByOption.none;
    },
    hideEmptyGroups() {
      return this.embed && !this.inEpicBoard
        ? this.embeddedBoardAttributes?.hideEmptyGroups
        : this.$store.getters.getHideEmptyGroups(this.currentBoardId);
    },
    readOnly() {
      return this.embeddedBoardAttributes?.readOnly || this.isGuestReader || false;
    },
    showAddButton() {
      return (
        typeof this.list.id !== 'undefined' &&
        this.list.id !== null &&
        !this.readOnly &&
        this.showAddCardButton &&
        !this.inEpicBoard
      );
    },
    showDraftCardCreateButton() {
      return (
        typeof this.list.id !== 'undefined' &&
        this.list.id !== null &&
        this.visible &&
        !this.readOnly &&
        !this.inEpicBoard
      );
    },
    isEpicBoard() {
      return this.currentBoardId === this.$constants.epicsBoardId;
    },
  },
  watch: {
    isCardsLayoutList: {
      handler(value) {
        this.$nextTick(() => {
          if (this.$refs?.listContent?.$el && this.scrollOps?.scrollPanel?.detectResize) {
            this.scrollOps.scrollPanel.detectResize = { scrollingX: false, scrollingY: !value };
          }
        });
      },
      immediate: true,
    },
    visible(value) {
      // scroll to last vertical position when visible again
      if (value) {
        this.scrollOps.vuescroll.detectResize = true;
        if (this.scrollTop > 0) {
          if (this.listCards.length > 0) {
            this.scrollListToLastPosition();
          } else {
            const listCardsWatch = this.$watch('listCards.length', (newValue) => {
              if (newValue > 0) {
                this.$nextTick(() => {
                  this.scrollListToLastPosition();
                });
                listCardsWatch();
              }
            });
          }
        }
      } else {
        this.scrollOps.vuescroll.detectResize = false;
      }
    },

    getHideEmptyGroups() {
      this.$emit('isEmptyGroup', this.isEmptyGroup);
    },
  },
  mounted() {
    this.$nextTick(() => {
      EventBus.$on('scrollToEnd', this.scrollToEnd);
      EventBus.$on('card:move', this.moveCardMessageHandler);
      EventBus.$on('card:add', this.addCardMessageHandler);
      if (this.index === 0) {
        clearTimeout(this.setLastHoverdListCardTimeout);
        this.setLastHoverdListCardTimeout = setTimeout(() => {
          this.$store.dispatch('lastHoveredListId', this.listId);
          if (this.listCards?.length > 0) {
            this.$store.dispatch('lastHoveredCardId', this.listCards[0]);
          }
        }, 200);
      }
    });
  },
  beforeUnmount() {
    EventBus.$off('scrollToEnd', this.scrollToEnd);
    EventBus.$off('card:move', this.moveCardMessageHandler);
    EventBus.$off('card:add', this.addCardMessageHandler);
    if (this.scrollingTimeout) {
      clearTimeout(this.scrollingTimeout);
    }
  },
  methods: {
    hideCreateCardView() {
      this.$emit('hideCreateCardView');
    },
    dragStart(evt) {
      requestAnimationFrame(() => {
        const entityName =
          this.currentBoardId === this.$constants.epicsBoardId
            ? ResourceType.Epic
            : ResourceType.Card;
        this.$store.dispatch('setDraggedEntity', {
          entityName,
          listId: this.list.id,
          cardId: evt.clone.dataset.cardId,
          boardId: this.currentBoard.id,
        });
      });
      if (this.$store.getters.getListDraftCardCreateView) {
        this.$store.dispatch('setListDraftCardCreateView', null);
      }
      this.hideCardActionsDropdown();
      this.hideMemberDropdown();
      if (this.isCardsLayoutList) {
        this.setDraggedElementPosition2('.dragging-card', -15, 0);
      }
      if (this.infoSidebar) {
        this.$store.dispatch('displayInfoSidebar', false);
      }
      if (this.sortByApplied) {
        this.$store.dispatch('setDropTargetId', `l-${this.listId}`);
      }
    },
    dragEnd() {
      this.onDraggingEnd();
      if (!this.dropTargetId) {
        this.clearProjectPreview();
      }
      this.$store.dispatch('setDraggedEntity', {});
      this.$store.dispatch('setDropTargetId', '');
    },
    onDragScroll(offsetX, offsetY, event, _eventTouch, element) {
      // distance from edge of window that we want to start scrolling
      const windowOffset = this.scrollSensitivity;
      const windowOffsetMin = windowOffset / 1000;
      const windowOffsetMax = 1 - windowOffsetMin;
      // position of the pointer within the element as a percentage
      const pointerElementX =
        (event.x - (window.innerWidth - element.offsetWidth)) / element.offsetWidth;
      const pointerElementY =
        (event.y - (window.innerHeight - element.offsetHeight)) / element.offsetHeight;
      const elementBounds = element.getBoundingClientRect();
      // distance of the pointer within the window offset as a percentage
      // right
      const scrollXMax = (event.x - (window.innerWidth - windowOffset)) / windowOffset;
      // left
      const scrollXMin = ((event.x - elementBounds.left) / windowOffset - 1) * -1;
      // bottom
      const scrollYMax = (event.y - (window.innerHeight - windowOffset)) / windowOffset;
      // top
      const scrollYMin = ((event.y - elementBounds.top) / windowOffset - 1) * -1;
      this.scrollSpeed = this.scrollSpeedBase;
      if (pointerElementX > windowOffsetMax) {
        // right
        this.scrollSpeed = this.scrollSpeedBase * scrollXMax;
      }
      if (pointerElementX < windowOffsetMin) {
        // left
        this.scrollSpeed = this.scrollSpeedBase * scrollXMin;
      }
      if (pointerElementY > windowOffsetMax) {
        // bottom
        this.scrollSpeed = this.scrollSpeedBase * scrollYMax;
      }
      if (pointerElementY < windowOffsetMin) {
        // top
        this.scrollSpeed = this.scrollSpeedBase * scrollYMin;
      }
      // handle miscalculations (e.g. resizing)
      if (this.scrollSpeed < 0) {
        this.scrollSpeed = 0;
      }
      if (this.scrollSpeed > this.scrollSpeedBase) {
        this.scrollSpeed = this.scrollSpeedBase;
      }
      element.scrollLeft += offsetX;
      element.scrollTop += offsetY;
    },
    cardHover({ cardId, listId }) {
      if (listId !== this.listId) {
        return;
      }
      this.$store.dispatch('lastHoveredListId', listId);
      this.$store.dispatch('lastHoveredCardId', cardId);
      if (
        this.$store.getters.selectedCardId !== cardId &&
        this.$store.getters.selectedCardId !== ''
      ) {
        this.$store.dispatch('setSelectedCardId', '');
      }
    },
    scrollToEnd(listId) {
      if (listId !== this.list.id) return;
      if (this.$refs.listContent) {
        this.$refs.listContent.scrollTop = this.$refs.listContent.scrollHeight;
      }
    },
    scrollCard({ dy = 0, speed = 300 }) {
      this.continueScroll = true;
      this.$refs.listContent.scrollBy(
        {
          dy,
        },
        speed,
        'easeInQuad'
      );
    },
    scrollBoardListToEnd(listId) {
      if (listId !== this.list.id) return;
      this.$refs.listContent.scrollTo({
        y: '100%',
      });
    },
    handleScroll(e) {
      if (!this.visible) return;
      this.scrollTop = e.scrollTop;
      this.setScrolling();
      this.hideCardEdit();
    },
    setScrolling() {
      if (this.scrolling) return;
      this.scrolling = true;
      this.scrollingTimeout = setTimeout(() => {
        this.scrolling = false;
        this.scrollingTimeout = null;
      }, 600);
    },
    hideCardEdit() {
      if (!this.continueScroll) {
        this.$store.dispatch('setCurrentCardIdForActionEdit', '');
        this.$store.dispatch('setCurrentCardIdMemberDropdown', '');
        this.$store.dispatch('setCurrentCardMembersDropdown', {});
      }
      setTimeout(() => {
        this.continueScroll = false;
      }, 300);
    },
    headerMouseEnter() {
      if (this.scrollTop && this.cardDragging) {
        const speed = this.scrollTop * 1.5;
        this.$refs.listContent.scrollTo({ y: 0 }, speed);
        this.scrollingUp = true;
      }
    },
    headerMouseOut() {
      if (this.scrollingUp) {
        this.$refs.listContent.nativeStop();
        this.scrollingUp = false;
      }
    },
    scrollListToLastPosition() {
      this.$refs.listContent.scrollTo({ y: this.scrollTop }, 'linear', 0);
    },
    mouseEnterList() {
      this.mouseOverList = true;
      this.scrollOnDrag =
        this.draggedEntity.entityName === 'list' || this.draggedEntity.entityName === 'card';
    },
    mouseLeaveList() {
      if (this.mouseEnterListTimeout) {
        clearTimeout(this.mouseEnterListTimeout);
      }
      this.mouseEnterListTimeout = setTimeout(() => {
        this.mouseOverList = false;
      }, 50);
      this.scrollOnDrag = false;
    },
  },
};
</script>
<style lang="scss">
$columnLeftRightPadding: $boardColumnPaddingLeft + $boardColumnPaddingRight;

.board-list-wrap {
  display: flex;
  flex-basis: $boardColumnWidth;
  flex-shrink: 0;
  flex-grow: 0;
  position: relative;
  min-width: $boardColumnWrapWidth;

  &.empty-group {
    flex-basis: 0 !important;
    min-width: 0;
    > * {
      display: none;
    }
  }

  .sort-background {
    @apply flex pt-2 justify-center absolute bg-draggElement-background top-10 rounded-lg text-sm font-normal text-deEmphasised-text;
    height: calc(100% - 50px);
    width: $boardColumnWrapWidth;
    z-index: 2;
  }
}

.list-content {
  .__bar-is-vertical {
    transition: none !important;
  }
  &.mouseEnter {
    &.__vuescroll {
      .__bar-is-vertical {
        opacity: 1 !important;
      }
    }
  }
  .draggable-cards {
    display: flex;
    flex-direction: column;
    align-items: center;
    position: relative;
    .cancel-drag {
      display: none;
    }
    .draggable-cards-header {
      position: absolute;
      top: 0;
      min-height: 66px;
      left: 0;
      right: 0;
      z-index: 3;
    }
    .drag-list-rows-wrap .list-row,
    .list-row {
      order: 3;
      margin-top: $boardCardTopMargin;
      margin-bottom: $boardCardBottomMargin;
      width: $columnWidth;
      z-index: 1;
      &.list-card-drop {
        border-radius: 8px;
        max-width: $columnWidth;
        box-shadow: unset;
        @apply bg-hover-onHover;
        > * {
          visibility: hidden;
        }
      }
      &.dragging-card,
      &.timeline-dragging-card {
        border-radius: 8px;
        opacity: 1 !important;
      }
      &.card-create-publish-button {
        margin-bottom: $boardCardGap;
        margin-top: 0;
      }
    }
    .static-header-ghost {
      order: 2;
    }
    .list-footer {
      order: 5;
      z-index: 1;
      position: sticky;
      bottom: 0;
      margin: 0 #{-$boardColumnPaddingRight} 0 #{-$boardColumnPaddingLeft};
      padding: $boardNewCardPaddingTop $boardColumnPaddingRight $boardNewCardPaddingBottom
        $boardColumnPaddingLeft;
      margin-top: 4px;
      @apply bg-background-strongest;
    }
    .epic-list-footer {
      height: $columnHeaderHeight;
      width: $columnWidth;
      @apply bg-background-strongest;
    }
    &.card-dragging {
      .list-header {
        z-index: 0;
      }
      > .list-row {
        margin-top: -56px;
        padding: 60px $boardColumnPaddingRight 0 $boardColumnPaddingLeft;
        margin-left: -$boardColumnPaddingLeft;
        margin-right: -$boardColumnPaddingRight;
        background-color: transparent;
        &.list-card-drop {
          .board-list-card-wrap {
            visibility: visible;
            box-shadow: none;
            border-color: transparent;
            @apply bg-hover-onHover;
            > * {
              visibility: hidden;
            }
          }
        }
        &.dragging-card,
        &.timeline-dragging-card {
          padding-left: 0px;
          padding-right: 0px;
          padding-top: 0;
          position: relative;
          .board-list-card-wrap {
            position: relative;
            .cancel-drag {
              position: absolute;
              top: 0;
              right: 0;
              display: block;
            }
          }
          .cancel-drag {
            display: flex;
          }
        }
        ~ .list-row {
          padding: 0px;
          margin-top: $boardCardTopMargin;
          margin-left: 0px;
          margin-right: 0px;
          width: calc(#{$columnWidth} + #{$columnLeftRightPadding});
          box-sizing: border-box;
          padding-left: $boardColumnPaddingLeft;
          padding-right: $boardColumnPaddingRight;
          &.list-card-drop {
            .board-list-card-wrap {
              visibility: visible;
              background-color: rgba(0, 0, 0, 0.04);
              box-shadow: none;
              border-color: transparent;
              @apply bg-hover-onHover;
              > * {
                visibility: hidden;
              }
            }
          }
          &.list-card-drop,
          &.dragging-card,
          &.timeline-dragging-card {
            padding-left: 0px;
            padding-right: 0px;
            padding-top: 0;
          }
        }
      }
    }
    &.list-scrolled-down {
      height: 100%;
      .list-header {
        z-index: 3;
        box-shadow: 0px 0px 20px -15px #212121;
      }
    }
    &.scroll-no-drag {
      transform: translateZ(0);
      .board-list-card-wrap {
        pointer-events: none;
      }
    }
  }
}
</style>
